import { Badge, Dropdown, Input } from '@ibsys/kit';
import { IconButton, Tooltip, TruncatedText } from '@/shared/ui';
import { EllipsisVerticalIcon, PencilIcon, SquarePlusIcon, SquareXIcon, Trash2Icon } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

type RolesMatrixRoleHeaderProps = {
  roleCode: string;
  roleName: string;
  isSystem: boolean;
  isFull: boolean;
  onToggleFullAccess: (roleCode: string, grant: boolean) => void;
  onDeleteRole: (roleCode: string) => void;
  onRenameRole: (roleCode: string, name: string) => void;
};

export const RolesMatrixRoleHeader: React.FC<RolesMatrixRoleHeaderProps> = ({
  roleCode,
  roleName,
  isSystem,
  isFull,
  onToggleFullAccess,
  onDeleteRole,
  onRenameRole,
}) => {
  const { t } = useTranslation('roles');
  const [isEditing, setIsEditing] = React.useState(false);
  const [draftName, setDraftName] = React.useState(roleName);

  const commitRename = () => {
    const next = draftName.trim();
    setIsEditing(false);
    if (!next || next === roleName) {
      setDraftName(roleName);
      return;
    }
    onRenameRole(roleCode, next);
  };

  const cancelRename = () => {
    setIsEditing(false);
    setDraftName(roleName);
  };

  const dropdownOptions = [
    {
      key: 'rename',
      title: (
        <div className={'flex gap-3 items-center'}>
          <PencilIcon />
          {t('renameRole')}
        </div>
      ),
      type: 'item' as const,
      onClick: () => {
        setDraftName(roleName);
        setIsEditing(true);
      },
    },
    {
      key: isFull ? 'revoke' : 'grant',
      title: (
        <div className={'flex gap-3 items-center'}>
          {isFull ? (
            <>
              <SquareXIcon />
              {t('revokeFullAccess')}
            </>
          ) : (
            <>
              <SquarePlusIcon />
              {t('grantFullAccess')}
            </>
          )}
        </div>
      ),
      type: 'item' as const,
      onClick: () => onToggleFullAccess(roleCode, !isFull),
    },
    ...(!isSystem
      ? [
          {
            key: 'delete',
            title: (
              <div className={'flex gap-3 items-center'}>
                <Trash2Icon />
                <span className={'text-destructive'}>{t('deleteRole')}</span>
              </div>
            ),
            type: 'item' as const,
            onClick: () => onDeleteRole(roleCode),
          },
        ]
      : []),
  ];

  return (
    <div className="flex items-center gap-2">
      <div className={'flex gap-1'}>
        {isEditing ? (
          <Input
            size={'sm'}
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitRename();
              if (e.key === 'Escape') cancelRename();
            }}
            autoFocus
            className="w-32"
          />
        ) : (
          <TruncatedText>{draftName}</TruncatedText>
        )}
        {isSystem && (
          <Tooltip tooltip={t('systemRole')}>
            <Badge outline className="text-xs font-light rounded-full size-2 p-2">
              {'S'}
            </Badge>
          </Tooltip>
        )}
      </div>
      <Dropdown options={dropdownOptions}>
        <IconButton
          size="icon"
          variant="ghost"
          className={'size-6'}
          label={t('roleActions')}
          icon={<EllipsisVerticalIcon />}
        />
      </Dropdown>
    </div>
  );
};
